/**
 * StripeProvider — wraps children in <Elements> with a lazily-loaded Stripe.js.
 *
 * The publishable key is fetched from the backend (GET /billing/publishable-key)
 * at runtime rather than baked into the bundle, matching TubeForge's admin-
 * configurable payments pattern.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { billingApi } from "@/api/billing.ts";

const stripeCache = new Map<string, Promise<Stripe | null>>();

function getStripe(pk: string): Promise<Stripe | null> {
  let cached = stripeCache.get(pk);
  if (!cached) {
    cached = loadStripe(pk);
    stripeCache.set(pk, cached);
  }
  return cached;
}

export function StripeProvider({
  clientSecret,
  children,
}: {
  clientSecret: string;
  children: ReactNode;
}) {
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    billingApi
      .publishableKey()
      .then((res) => {
        if (!cancelled) setPublishableKey(res.publishableKey);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(
            err?.message || "Payment system is not available right now.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stripePromise = useMemo(
    () => (publishableKey ? getStripe(publishableKey) : null),
    [publishableKey],
  );

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!publishableKey || !stripePromise) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-fgMuted">
        Loading secure payment form…
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: { theme: "stripe" } }}
    >
      {children}
    </Elements>
  );
}
